import AddIcon from '@mui/icons-material/Add';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChairRoundedIcon from '@mui/icons-material/ChairRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import WaterDamageRoundedIcon from '@mui/icons-material/WaterDamageRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import WavesRoundedIcon from '@mui/icons-material/WavesRounded';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

import { useSelector } from 'react-redux';

import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import { useGetGatewaysByAccountQuery } from '@/api/recalc/endpoints/gatewayApiSlice';

import HtmlTooltip from '@/components/tooltip/HtmlTooltip';

const getIconForKey = (key) => {
  // This function can be expanded with more icons based on key
  switch (key) {
    case 'ColdWaterSupplyAirTemp':
      return (
        <AirRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(209, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'ColdWaterSupplyPipeTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            transform: 'rotate(90deg)',
            color: 'hsla(209, 100%, 63%, 1)',
            mr: 1,
          }}
        />
      );
    case 'SupplyPipeFlow':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            transform: 'rotate(90deg)',
            color: 'hsla(209, 100%, 63%, 1)',
            mr: 1,
          }}
        />
      );
    case 'ReturnPipeFlow':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            transform: 'rotate(90deg)',
            color: 'hsla(209, 100%, 63%, 1)',
            mr: 1,
          }}
        />
      );
    case 'PipeFlow':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            transform: 'rotate(90deg)',
            color: 'hsla(209, 100%, 63%, 1)',
            mr: 1,
          }}
        />
      );
    case 'ComfortSensorTemp':
      return <ChairRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    case 'HeatingPipeSupplyTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            color: 'hsla(8, 100%, 63%, 1)',
            transform: 'rotate(90deg)',
            mr: 1,
          }}
        />
      );
    case 'HeatingPipeReturnTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            color: 'hsla(8, 100%, 63%, 1)',
            transform: 'rotate(90deg)',
            mr: 1,
          }}
        />
      );
    case 'HotWaterSupplyPipeTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            color: 'hsla(8, 100%, 63%, 1)',
            transform: 'rotate(90deg)',
            mr: 1,
          }}
        />
      );
    case 'HotWaterSupplyAirTemp':
      return (
        <AirRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(8, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'HeatingPipeTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            color: 'hsla(8, 100%, 63%, 1)',
            transform: 'rotate(90deg)',
            mr: 1,
          }}
        />
      );
    case 'PowerConsumption':
      return (
        <BoltRoundedIcon
          fontSize="small"
          sx={{ color: 'hsl(50, 100%, 50%)', mr: 1 }}
        />
      );
    case 'WaterConsumption':
      return (
        <WaterDropRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(209, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'CO2Level':
      return <Co2RoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    case 'RelativeHumidity':
      return <WaterDamageRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    // Add more cases for different keys with different icons as needed
    default:
      return <QuestionMarkRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
  }
};

const createAlarmResponseList = (response) => {
  if (!response || (!response.warnings?.length && !response.errors?.length)) {
    return <Typography>No warnings or errors.</Typography>;
  }

  const warningsList = response.warnings?.map((warning, index) => (
    <ListItem key={`warning-${index}`}>
      <ListItemText
        primary={warning.warning_type}
        primaryTypographyProps={{ color: 'text.primary' }}
        secondary={warning.warning_message}
        secondaryTypographyProps={{ color: 'text.secondary' }}
      />
    </ListItem>
  ));

  const errorsList = response.errors?.map((error, index) => (
    <ListItem key={`error-${index}`}>
      <ListItemText
        primary={error.error_type}
        primaryTypographyProps={{ color: 'text.primary' }}
        secondary={error.error_message}
        secondaryTypographyProps={{ color: 'text.secondary' }}
      />
    </ListItem>
  ));

  return (
    <>
      {warningsList?.length > 0 && (
        <>
          <HtmlTooltip
            title={
              <ListItemIcon>
                <WarningRoundedIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  Warnings were created when creating default alarms.
                </Typography>
              </ListItemIcon>
            }
            content={warningsList}
          >
            <ListItemIcon>
              <WarningRoundedIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Warnings were created when creating default alarms.
              </Typography>
            </ListItemIcon>
          </HtmlTooltip>
        </>
      )}
      {errorsList?.length > 0 && (
        <>
          <HtmlTooltip
            title="Warnings are issues that may not prevent the creation of default alarms, but are simply observations made during the creation process."
            content={errorsList}
          >
            <ListItemIcon>
              <WarningRoundedIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Errors were created when creating default alarms.
              </Typography>
            </ListItemIcon>
          </HtmlTooltip>
        </>
      )}
    </>
  );
};

const JobSubscriptionCreate = ({
  open,
  onClose,
  submit,
  building,
  location,
  jobSpecifications,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    JobType: '',
    DataInput: {},
    UserInput: {
      Location: location || { Address: '', Latitude: 0, Longitude: 0 },
      ControlType: 'pct',
      BuildingOccupiedCalendar: [],
      BuildingEmptyCalendar: [],
    },
    AccountId: building?.AccountId || 0,
    DisplayName: '',
  });

  const account = useSelector(getSelectedAccount);

  const { data: units } = useGetUnitsByAccountQuery(account.AccountId, {
    skip: !account.AccountId,
  });

  const { data: gateways } = useGetGatewaysByAccountQuery(account.AccountId, {
    skip: !account.AccountId,
  });

  const [showOptionalFields, setShowOptionalFields] = useState({});

  const [selectedAlarmJobTypes, setSelectedAlarmJobTypes] = useState([]);
  const [selectedPeoJobTypes, setSelectedPeoJobTypes] = useState([]);
  const [selectAllAlarmJobTypes, setSelectAllAlarmJobTypes] = useState(true);

  const handleJobTypeChange = (event) => {
    setFormData({ ...formData, JobType: event.target.value });
  };

  const handleDataInputChange = (key, index, field, value) => {
    setFormData((prevData) => {
      const newDataInput = { ...prevData.DataInput };
      if (!newDataInput[key]) {
        newDataInput[key] = [];
      }
      if (!newDataInput[key][index]) {
        newDataInput[key][index] = {};
      }
      newDataInput[key][index][field] = value;

      // Set DataType based on UnitTypeInputId
      if (field === 'UnitTypeInputId') {
        const unit = units?.find(
          (unit) => unit.UnitId === newDataInput[key][index].UnitId,
        );
        const selectedSensor = unit?.UnitSensors?.find(
          (sensor) =>
            sensor.UnitTypeInput.UnitTypeInputId ===
            newDataInput[key][index].UnitTypeInputId,
        );
        if (selectedSensor) {
          const validDataTypes =
            selectedSensor.UnitTypeInput.SensorRequirements?.map(
              (req) => req.DataType,
            );
          if (validDataTypes.length === 1) {
            newDataInput[key][index].DataType = validDataTypes[0];
          } else {
            newDataInput[key][index].DataType = '';
          }
        }
      }

      // Set AggregateType if it's not already set
      if (!newDataInput[key][index]?.AggregateType) {
        newDataInput[key][index].AggregateType =
          jobSpecifications
            ?.find((job) => job.JobType === formData.JobType)
            ?.DataInputSpecifications?.find((spec) => spec.Key === key)
            ?.DefaultAggregateType || 'Minutes5';
      }

      return { ...prevData, DataInput: newDataInput };
    });
  };

  const handleUserInputChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      UserInput: { ...prevData.UserInput, [key]: value },
    }));
  };

  const toggleOptionalField = (key) => {
    setShowOptionalFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

    if (formData.DataInput[key]) {
      setFormData((prevData) => {
        const newDataInput = { ...prevData.DataInput };
        delete newDataInput[key];
        return { ...prevData, DataInput: newDataInput };
      });
    }
  };

  const renderDataInputFields = (specifications) => {
    return specifications.DataInputSpecifications?.map((spec) => {
      const isFieldEnabled = showOptionalFields[spec.Key] || spec.Required;

      return (
        <Box key={spec.Key} mb={2}>
          <Grid container alignItems="center">
            <Grid item sx={{ mb: 1 }}>
              {getIconForKey(spec.Key)}
            </Grid>
            <Grid item xs>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {spec.DisplayName}
              </Typography>
            </Grid>
            {!spec.Required && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isFieldEnabled}
                      onChange={() => toggleOptionalField(spec.Key)}
                    />
                  }
                  label={`Enable`}
                />
              </Grid>
            )}
          </Grid>

          {isFieldEnabled && (
            <FormGroup>
              {[...Array(formData.DataInput[spec.Key]?.length || 1)]?.map(
                (_, index) => (
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 1 }}
                    key={`${spec.Key}-${index}`}
                    alignItems="center"
                  >
                    <Grid item xs={4}>
                      <Autocomplete
                        options={units}
                        getOptionLabel={(option) => option.Name} // Use only Name as the text part for filtering
                        filterOptions={(options, { inputValue }) => {
                          return options?.filter(
                            (option) =>
                              option.Name.toLowerCase().includes(
                                inputValue.toLowerCase(),
                              ) ||
                              option.UnitId.toString().includes(inputValue),
                          );
                        }}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Chip
                              icon={<LightRoundedIcon />}
                              label={option.UnitId}
                              style={{
                                marginRight: 8,
                                borderRadius: 1,
                              }}
                              size="small"
                            />
                            {option.Name}
                          </li>
                        )}
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'UnitId',
                            value?.UnitId || '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs>
                      <Autocomplete
                        options={
                          units?.find(
                            (unit) =>
                              unit.UnitId ===
                              formData.DataInput[spec.Key]?.[index]?.UnitId,
                          )?.UnitSensors || []
                        }
                        getOptionLabel={(option) =>
                          option.CustomName || option.UnitTypeInput.Name
                        }
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'UnitTypeInputId',
                            value?.UnitTypeInput.UnitTypeInputId || '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit Sensor"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs>
                      <Autocomplete
                        options={['Minutes5', 'Hour']}
                        value={
                          formData.DataInput[spec.Key]?.[index]
                            ?.AggregateType || 'Minutes5'
                        }
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'AggregateType',
                            value,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Aggregate Type"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs>
                      <Autocomplete
                        options={
                          units
                            ?.find(
                              (unit) =>
                                unit.UnitId ===
                                formData.DataInput[spec.Key]?.[index]?.UnitId,
                            )
                            ?.UnitSensors.find(
                              (sensor) =>
                                sensor.UnitTypeInput.UnitTypeInputId ===
                                formData.DataInput[spec.Key]?.[index]
                                  ?.UnitTypeInputId,
                            )
                            ?.UnitTypeInput.SensorRequirements.map(
                              (req) => req.DataType,
                            ) || []
                        }
                        value={
                          formData.DataInput[spec.Key]?.[index]?.DataType || ''
                        }
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'DataType',
                            value,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Data Type"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={0.4} container justifyContent="flex-end">
                      {spec.AllowMultiple && (
                        <IconButton
                          sx={{ borderRadius: 1, ml: 1 }}
                          onClick={() =>
                            setFormData((prevData) => {
                              const newDataInput = { ...prevData.DataInput };
                              if (index === 0) {
                                newDataInput[spec.Key] = [
                                  ...(newDataInput[spec.Key] || []),
                                  {},
                                ];
                              } else {
                                newDataInput[spec.Key] = newDataInput[
                                  spec.Key
                                ]?.filter((_, i) => i !== index);
                              }
                              return { ...prevData, DataInput: newDataInput };
                            })
                          }
                        >
                          {index === 0 ? <AddIcon /> : <RemoveIcon />}
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ),
              )}
            </FormGroup>
          )}
        </Box>
      );
    });
  };

  const renderUserInputFields = () => {
    const controlType = formData.UserInput.ControlType || 'pct';
    const jobSpec = jobSpecifications.find(
      (job) => job.JobType === formData.JobType,
    );

    if (
      formData.JobType !== 'PredictiveHeatControl' &&
      formData.JobType !== 'PredictiveCooling'
    ) {
      // If the job type is neither PredictiveHeatControl nor PredictiveCooling, do not render the control type fields
      return null;
    }

    return (
      <Box>
        <Box display="flex">
          <RouterRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body1">Controller Settings</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth margin="normal">
              <RadioGroup
                value={controlType}
                onChange={(e) =>
                  handleUserInputChange('ControlType', e.target.value)
                }
                row // This makes the radio buttons appear in a row; remove if you want them in a column
              >
                <FormControlLabel
                  value="pct"
                  control={<Radio size="small" />}
                  label="PCT"
                />
                <FormControlLabel
                  value="ecl"
                  control={<Radio size="small" />}
                  label="ECL"
                />
                <FormControlLabel
                  value="bravida"
                  control={<Radio size="small" />}
                  label="Bravida"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        {controlType === 'pct' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <Autocomplete
                options={gateways}
                getOptionLabel={(option) => option.Name}
                onChange={(e, value) =>
                  handleUserInputChange('GatewayId', value?.GatewayId || '')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gateway"
                    variant="filled"
                    size="small"
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={[
                  { value: 'output0', label: 'Temperature probe override' },
                  { value: 'output1', label: 'Relay 1' },
                  { value: 'output2', label: 'Relay 2' },
                  { value: 'output3', label: 'Relay 3' },
                ]}
                getOptionLabel={(option) => option.label}
                onChange={(e, value) =>
                  handleUserInputChange('Channel', value?.value || '')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Actuator Channel"
                    variant="filled"
                    size="small"
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        {controlType === 'ecl' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <TextField
                label="Device Number"
                type="number"
                variant="filled"
                fullWidth
                size="small"
                onChange={(e) =>
                  handleUserInputChange('DeviceNumber', Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Circuit Number"
                type="number"
                variant="filled"
                fullWidth
                size="small"
                onChange={(e) =>
                  handleUserInputChange('CircuitNumber', Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Timezone Offset"
                variant="filled"
                fullWidth
                size="small"
                onChange={(e) =>
                  handleUserInputChange('Offset', Number(e.target.value))
                }
              />
            </Grid>
          </Grid>
        )}

        {controlType === 'bravida' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <TextField
                label="Building Name"
                variant="filled"
                fullWidth
                size="small"
                onChange={(e) =>
                  handleUserInputChange('BuildingName', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Heating Circuit Name"
                variant="filled"
                fullWidth
                size="small"
                onChange={(e) =>
                  handleUserInputChange('HeatingCircuitName', e.target.value)
                }
              />
            </Grid>
          </Grid>
        )}

        {jobSpec?.UserInputSpecifications.some(
          (spec) => spec.Key === 'HeatingSystemType',
        ) && (
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Heating System Type</InputLabel>
                <Select
                  variant="filled"
                  value={formData.UserInput.HeatingSystemType || ''}
                  onChange={(e) =>
                    handleUserInputChange('HeatingSystemType', e.target.value)
                  }
                >
                  {jobSpec.UserInputSpecifications.find(
                    (spec) => spec.Key === 'HeatingSystemType',
                  )?.InputParameters.ValidValues.map((option) => (
                    <MenuItem key={option.Value} value={option.Value}>
                      {option.Text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {jobSpec?.UserInputSpecifications.some(
          (spec) => spec.Key === 'RiskOfFrostDamage',
        ) && (
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" size="small">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.UserInput.RiskOfFrostDamage || false}
                      onChange={(e) =>
                        handleUserInputChange(
                          'RiskOfFrostDamage',
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Risk of Frost Damage"
                />
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  const validateForm = () => {
    const jobSpec = jobSpecifications.find(
      (job) => job.JobType === formData.JobType,
    );
    let isValid = true;

    // Validate DataInput fields
    jobSpec?.DataInputSpecifications.forEach((spec) => {
      const inputs = formData.DataInput[spec.Key];
      if (spec.Required && (!inputs || inputs.length === 0)) {
        isValid = false;
        alert(`Missing required data input for ${spec.DisplayName}`);
      }
      inputs?.forEach((input, index) => {
        if (
          !input.UnitId ||
          !input.UnitTypeInputId ||
          !input.DataType ||
          !input.AggregateType
        ) {
          isValid = false;
          alert(
            `Missing required fields for ${spec.DisplayName} entry ${
              index + 1
            }`,
          );
        }
      });
    });

    // Validate UserInput fields
    if (
      formData.JobType === 'PredictiveHeatControl' ||
      formData.JobType === 'PredictiveCooling'
    ) {
      if (formData.UserInput.ControlType === 'pct') {
        if (!formData.UserInput.GatewayId || !formData.UserInput.Channel) {
          isValid = false;
          alert('Missing required user input for PCT controller');
        }
      } else if (formData.UserInput.ControlType === 'ecl') {
        if (
          !formData.UserInput.DeviceNumber ||
          !formData.UserInput.CircuitNumber ||
          !formData.UserInput.Offset
        ) {
          isValid = false;
          alert('Missing required user input for ECL controller');
        }
      }
    }

    // Validate HeatingSystemType if it is required
    if (
      jobSpec?.UserInputSpecifications.some(
        (spec) => spec.Key === 'HeatingSystemType' && spec.Required,
      )
    ) {
      if (!formData.UserInput.HeatingSystemType) {
        isValid = false;
        alert('Missing required user input for Heating System Type');
      }
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const finalFormData = { ...formData };

      // Remove fields not relevant for the selected job type
      if (
        formData.JobType !== 'PredictiveHeatControl' &&
        formData.JobType !== 'PredictiveCooling'
      ) {
        delete finalFormData.UserInput.ControlType;
        delete finalFormData.UserInput.GatewayId;
        delete finalFormData.UserInput.Channel;
        delete finalFormData.UserInput.DeviceNumber;
        delete finalFormData.UserInput.CircuitNumber;
        delete finalFormData.UserInput.Offset;
      }

      submit(finalFormData);
    }
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      JobType: '',
      DataInput: {},
      UserInput: {
        Location: location || { Address: '', Latitude: 0, Longitude: 0 },
        ControlType: 'pct',
        BuildingOccupiedCalendar: [],
        BuildingEmptyCalendar: [],
      },
      AccountId: building?.AccountId || 0,
      DisplayName: '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Create Job Subscription on account: {account.Name}{' '}
      </DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth margin="normal" size="small">
          <Autocomplete
            options={jobSpecifications}
            getOptionLabel={(option) => option.DisplayName}
            onChange={(e, value) =>
              handleJobTypeChange({
                target: { value: value?.JobType || '' },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Type"
                variant="filled"
                size="small"
              />
            )}
          />
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Display Name"
          variant="filled"
          size="small"
          value={formData.DisplayName}
          onChange={(e) =>
            setFormData({ ...formData, DisplayName: e.target.value })
          }
        />

        {formData.JobType && (
          <Grid container spacing={4}>
            <Grid item xs={12} lg={12}>
              {renderDataInputFields(
                jobSpecifications.find(
                  (job) => job.JobType === formData.JobType,
                ),
              )}
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {formData.JobType === 'PredictiveCooling' ||
            formData.JobType === 'PredictiveHeatControl' ? (
              <Grid item xs={12} lg={12}>
                {renderUserInputFields()}
              </Grid>
            ) : null}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <Button variant="text" size="small" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} size="small">
          {isLoading ? (
            <CircularProgress color="inherit" size={40} />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobSubscriptionCreate;
